import { defineMetadata, getMetadata } from 'reflect-metadata/no-conflict';
import {
	ArrayConversions,
	Asset,
	AssetKey,
	Company,
	CompanyKey,
	convertToValidWebsiteUrl,
	DynamicChoiceKey,
	EditItemType,
	ensureValueIsBoolean,
	getAssetUpdateOptions,
	getCompanyUpdateOptions,
	LocalizedTextIds,
	ReferenceValueKey,
	UpdateConversion,
} from '../index.js';

export const BlueKnightKey = Symbol('BlueKnight');
export const MetadataKey = Symbol('Metadata');
export const UpdateableKey = Symbol('Updateable');
export const UpdateableOptionsKey = Symbol('UpdateableOptions');

// In the future there could be more than one currency per company,
// so it seemed easier to just wire it in now to load it from a dynamic field
export type CurrencyKey = 'currencyBlueKnight';

export interface UpdateOptions {
	editType?: EditItemType;
	promptTextId?: LocalizedTextIds;
	labelTextId?: LocalizedTextIds;
	footnoteId?: LocalizedTextIds;
	footnoteCountries?: string[];
	tooltipId?: LocalizedTextIds;
	subtitleId?: LocalizedTextIds;
	isJnjConfidentialInfo?: boolean;
	isReadOnly?: boolean;
	maxLength?: number;
	applyDeiCountryPrefix?: boolean;
	referenceDataKey?: ReferenceValueKey;
	showLength?: boolean;
	required?: boolean;
	preconditionProp?: CompanyKey | AssetKey;
	// FUTURE - this is also used at ingest for non-updateable fields and may warrant
	// moving to the metadata
	updateConversion?: UpdateConversion;
	delimiter?: string;
	currencyKey?: CurrencyKey;
	updateHeaderKey?: LocalizedTextIds;

	/** Some items have choices that cannot be defined at transpile time.
	 * Too add one of these, establish a new DynamicChoiceKey, decorate the
	 * property with it and add the code to return the choices in edit-item.component.ts
	 *
	 * Future - It is possible these could be implemented with a decorator member that
	 * is a function with a Company param (and maybe a config object?), but that was not attempted at the initial
	 * decorator implementation
	 */
	dynamicChoiceKey?: DynamicChoiceKey;
}

export interface Metadata {
	header?: LocalizedTextIds;
	indexable?: boolean;
}

export function BlueKnight<T, K extends keyof T & string>() {
	return function (target: T, propertyKey: K): void {
		const properties: K[] = getMetadata(BlueKnightKey, target);

		if (properties) {
			properties.push(propertyKey);
		} else {
			defineMetadata(BlueKnightKey, [propertyKey], target);
		}
	};
}

export function getBlueKnightFieldsRaw<T, K extends keyof T & string>(
	target: T
): K[] {
	return getMetadata(BlueKnightKey, target);
}

export function Updateable<T, K extends keyof T & string>(
	options: UpdateOptions = {}
) {
	return function (target: T, propertyKey: K): void {
		const properties: K[] = getMetadata(UpdateableKey, target);

		if (properties) {
			properties.push(propertyKey);
		} else {
			defineMetadata(UpdateableKey, [propertyKey], target);
		}

		defineMetadata(UpdateableOptionsKey, options, target, propertyKey);
	};
}

export function getUpdateableFieldsRaw<T, K extends keyof T & string>(
	target: T
): K[] {
	return getMetadata(UpdateableKey, target);
}

export function Metadata<T, K extends keyof T & string>(options: Metadata) {
	return function (target: T, propertyKey: K): void {
		defineMetadata(MetadataKey, options, target, propertyKey);
	};
}
export function getUpdateOptionsRaw<T, K extends keyof T & string>(
	propertyKey: K,
	target: T
): UpdateOptions {
	return getMetadata(UpdateableOptionsKey, target, propertyKey);
}

export function getMetadataRaw<T, K extends keyof T & string>(
	propertyKey: K,
	target: T
): Metadata {
	return getMetadata(MetadataKey, target, propertyKey);
}

export function getUpdateOptions(
	field: CompanyKey | AssetKey,
	type: typeof Company | typeof Asset
): UpdateOptions {
	switch (type) {
		case Company:
			return getCompanyUpdateOptions(field as CompanyKey);
		case Asset:
			return getAssetUpdateOptions(field as AssetKey);
		default:
			return {};
	}
}

export function convertFieldFromDB(
	field: CompanyKey | AssetKey,
	originalValue: string,
	type: typeof Company | typeof Asset = Company
): unknown {
	const updateOptions = getUpdateOptions(field, type);

	// Still convert empty string values to handled data edge cases such as "empty string dates" (ADJQ-2035)
	if (updateOptions?.updateConversion == undefined || originalValue == null) {
		return ArrayConversions.includes(updateOptions.updateConversion)
			? []
			: originalValue;
	}

	// For some reason typescript thinks updateConversion in not of type UpdateConversion
	// but instead is a union of all options in the enum except BooleanToYesNo
	switch (updateOptions.updateConversion as UpdateConversion) {
		case UpdateConversion.DelimitedString:
			return splitString(originalValue, updateOptions.delimiter ?? ';');
		case UpdateConversion.CountryPrefixedDelimitedString:
			return splitString(
				originalValue,
				updateOptions.delimiter ?? ';'
			).map((val) => (val.includes(' - ') ? val.split(' - ')[1] : val));
		case UpdateConversion.CorrectedWhitespace:
			return originalValue?.replace(/\s\s+/g, ' ').trim() ?? '';
		case UpdateConversion.ValidWebsite:
			return convertToValidWebsiteUrl(originalValue);
		case UpdateConversion.DateString:
			return originalValue ? new Date(originalValue as string) : null;
		case UpdateConversion.BooleanToYesNo:
			return ensureValueIsBoolean(originalValue);
		default:
			return originalValue;
	}
}

export function IsArrayProperty(
	key: CompanyKey | AssetKey,
	type: typeof Company | typeof Asset
): boolean {
	const options =
		type === Company
			? getCompanyUpdateOptions(key as CompanyKey)
			: getAssetUpdateOptions(key as AssetKey);
	return ArrayConversions.includes(options.updateConversion);
}

export function splitString(val: string, delimiter: string): string[] {
	if (!val) {
		return [];
	}
	const values = val.includes(delimiter) ? val.split(delimiter) : [val];
	return values.map((v) => {
		return v.trim();
	});
}
